import { formatHeaderDate } from "@/lib/date-format";
import type { ExertionZone } from "@/lib/recovery/formula";

/**
 * Time-of-day-aware greeting for Today (SPEC 3.1.1 / 3.1.2).
 * Subtitle shows date in `Thu 30/04` style + count of upcoming milestones in
 * 7d. HOTFIX-BW19.4 PR-C Bug 9: the Recovery card is being demoted below
 * the fold, so we surface a tiny Recovery <score> chip on the greeting line
 * with the same color band as the zone. Keeps the score one glance away.
 */
export function Greeting({
  name,
  upcomingIn7d,
  recoveryScore,
  recoveryZone,
}: {
  name: string;
  upcomingIn7d?: number;
  recoveryScore?: number | null;
  recoveryZone?: ExertionZone | null;
}) {
  const now = new Date();
  const h = now.getHours();
  const part =
    h < 5 ? "evening" : h < 12 ? "morning" : h < 17 ? "afternoon" : "evening";

  const headerDate = formatHeaderDate(now);
  const [day, dm] = headerDate.split(" ");

  return (
    <header className="haus-greeting">
      <style>{`
        .haus-greeting { margin-bottom: var(--space-6); }
        @media (max-width: 767px) {
          .haus-greeting { margin-bottom: var(--space-4); margin-top: 0; }
          .haus-greeting h1 { font-size: 26px !important; line-height: 1.15 !important; }
        }
      `}</style>
      <h1
        style={{
          fontFamily: "var(--font-sans)",
          fontWeight: 500,
          fontSize: "var(--type-display-m)",
          margin: 0,
          letterSpacing: "-0.005em",
        }}
      >
        Good {part}, <span className="serif-italic">{name}</span>.
      </h1>
      <p
        style={{
          color: "var(--fg-3)",
          fontSize: "var(--type-body-s)",
          margin: "var(--space-2) 0 0",
          fontFamily: "var(--font-sans)",
        }}
      >
        <span className="serif-italic">{day}.</span>{" "}
        <span className="mono">{dm}</span>
        {recoveryScore != null ? (
          <>
            {" · "}
            <span
              className="mono"
              aria-label={`Recovery ${recoveryScore} of 100`}
              style={{
                display: "inline-block",
                padding: "1px 8px",
                marginLeft: 2,
                borderRadius: 999,
                background: zoneChipBg(recoveryZone ?? null),
                color: zoneChipFg(recoveryZone ?? null),
                fontSize: 11,
                lineHeight: 1.5,
                verticalAlign: "1px",
              }}
            >
              Recovery {recoveryScore}
            </span>
          </>
        ) : null}
        {upcomingIn7d && upcomingIn7d > 0 ? (
          <>
            {" · "}
            <span className="mono">{upcomingIn7d}</span> thing
            {upcomingIn7d > 1 ? "s" : ""} in next 7 days
          </>
        ) : null}
      </p>
    </header>
  );
}

function zoneChipBg(zone: ExertionZone | null): string {
  switch (zone?.band) {
    case "rest":
      return "color-mix(in oklab, var(--brick) 16%, transparent)";
    case "easy":
      return "color-mix(in oklab, var(--amber, #d97706) 18%, transparent)";
    case "moderate":
      return "color-mix(in oklab, var(--severity-ok) 16%, transparent)";
    case "quality":
    case "push":
      return "color-mix(in oklab, var(--severity-ok) 22%, transparent)";
    default:
      return "var(--bg-3)";
  }
}

function zoneChipFg(zone: ExertionZone | null): string {
  switch (zone?.band) {
    case "rest":
      return "var(--brick)";
    case "easy":
      return "var(--amber, #d97706)";
    case "moderate":
    case "quality":
    case "push":
      return "var(--severity-ok)";
    default:
      return "var(--fg-2)";
  }
}
